import TabSystem from '../../components/TabSystem';
import { Text } from '../../components/typography';
import styles from './Quality.module.scss';
import { Button } from '../../components';
import { useQueryParams } from '../../hooks/misc';
import {
  defaultAssessmentTypes,
  searchParamKeys,
} from '../../constants/strings';
import { useModal } from '../../providers/ModalProvider';
import { modals } from '../../providers/modals';
import { useCurrentUser } from '../../providers/UserProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { loader } from 'graphql.macro';
import { gql, useQuery } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { actions } from '../../utilities';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import AccessDeniedPage from '../../components/AccessDeniedPage';
import CustomSpinner from '../../components/CustomSpinner';
import { fetchAllData } from '../../utilities/excel_export/table_excel_export.js';
import { useState } from 'react';
import { showToast } from '../../graphql/cache/modal.js';
import { toastVariant } from '../../constants/misc.js';
import { exportToExcel } from '../../components/excel/ExcelExport.jsx';
import {
  excelHeaderLookup,
  formatAssessment,
} from '../../utilities/excel_export/data_converter.js';
import format from 'date-fns/format';
import ProgressModal from '../../components/modals/ProgressModal.jsx';
import { useURLParams } from '../../providers/URLParamProvider.jsx';
import InspectionsList from '../fleet/InspectionsList.jsx';
import useCheckPermission from '../../providers/UsePermissionHook.jsx';
import TemplatesList from '../fleet/TemplatesList.jsx';

const defaultTab = 'quality';
const inspectionExportQuery = loader(
  'src/pages/fleet/Inspections.fetch.graphql',
);
const checkPermissionQuery = loader(
  '../../providers/UsePermissionHook.graphql',
);

export default function QualityModule({ disabled = false }) {
  const { user, isAdmin } = useCurrentUser();
  const online = useOnlineStatus();
  const { data: permissionData, loading: permissionLoading } = useQuery(
    checkPermissionQuery,
    {
      skip: !user?.id || !online,
      variables: { modelName: 'quality', action: actions.view },
    },
  );
  const hasAccess = !!permissionData?.checkPermission;
  // One grant covers both QUALITY and MOC templates, so checking one is enough
  const canManageTemplates = useCheckPermission({
    model: 'inspectionTemplate',
    action: actions.manageTemplate,
    type: 'QUALITY',
  });
  const { getParam } = useQueryParams();
  const { openModal } = useModal();
  const tab = getParam(searchParamKeys.tab, defaultTab);
  const { filters, subfilters } = useURLParams();
  const { t } = useTranslation();
  const client = useApolloClient();
  const [isFetchingAllData, setIsFetchingAllData] = useState(false);
  const [progress, setProgress] = useState(0);
  const { workspace, allWorkspaces, isWorkspaceAdmin } = useWorkspace();

  const handleExport = async () => {
    const query = gql`
      ${inspectionExportQuery}
    `;
    await fetchAllData({
      query,
      client,
      setIsFetching: setIsFetchingAllData,
      table: 'inspections',
      count: 'inspectionsCount',
      filters,
      subfilters,
      setProgress,
    }).then((response, errors) => {
      if (errors?.length) {
        showToast({
          title: 'Error Exporting Data',
          variant: toastVariant.danger,
          message: `An error occurred: ${errors[0].message}`,
        });
      }
      if (response?.length) {
        const data = response.map((r) => {
          const workspaceData = allWorkspaces?.find(
            (w) => w.id === r.workspaceId,
          );
          const reportedWorkspaceData = allWorkspaces?.find(
            (w) => w.id === r.reportedWorkspaceId,
          );

          return {
            ...r,
            reportedWorkspace: reportedWorkspaceData,
            workspace: workspaceData,
          };
        });
        const flattenedData = data.flatMap((d) => {
          const reportedWorkspaceTitle = allWorkspaces?.find(
            (w) => w?.id === d?.workspaceId,
          )?.title;
          const formattedData = formatAssessment({
            ...d,
            reportedWorkspaceTitle,
          });
          const creatorId = d?.creator?.id;
          const filteredParticipants = (d.participants || []).filter(
            (participant) => participant.id !== creatorId,
          );
          const rowsWithParticipants = filteredParticipants?.map(
            (participant) => ({
              ...formattedData,
              participant: 'TRUE',
              creator: `${participant.firstName} ${participant.lastName}`,
              creatorId: `${participant.id}`,
              creatorEmail: participant.email,
              workspace: `${workspace?.title}`,
            }),
          );
          return [formattedData, ...rowsWithParticipants];
        });

        exportToExcel({
          data: flattenedData,
          sheetName: 'Assessment Data',
          exportedBy: `${user.firstName} ${user.lastName}`,
          type: 'Assessment',
          fileName: `Assessment_Export_${format(new Date(), 'yyyy-MM-dd')}`,
          reportType: ['Report Type', 'Assessment Table Export'],
          headerMap: excelHeaderLookup,
          workspace: `${workspace?.title}`,
        });
      }
      if (!response.length) {
        showToast({
          title: 'Error Exporting Data',
          variant: toastVariant.danger,
          message: `No Data to export.`,
        });
      }
    });
  };

  const options = [
    {
      title: 'Quality',
      element: InspectionsList,
      key: 'quality',
    },
    {
      title: 'MOC',
      element: InspectionsList,
      key: 'moc',
    },
    canManageTemplates && {
      title: t(keys.common.TEMPLATES),
      element: TemplatesList,
      key: 'templates',
    },
  ].filter(Boolean);

  if (permissionLoading) {
    return <CustomSpinner text="Checking access..." />;
  }

  if (!hasAccess) {
    return <AccessDeniedPage grantName="Access" />;
  }

  return (
    <div className={disabled ? styles.disabled : null}>
      <div className={styles.headerContainer}>
        <Text size="lg" weight="bold" noMargin>
          Quality
        </Text>
        <div className={styles.buttons}>
          {(tab !== 'templates' || canManageTemplates) && (
            <Button
              value={t(keys.common.NEW)}
              variant="primary"
              icon="add"
              size="md"
              onClick={() => {
                switch (tab) {
                  case 'quality':
                    openModal({
                      modalName: modals.createAssessment,
                      variables: {
                        participants: [user],
                        types: [defaultAssessmentTypes.quality],
                        type: defaultAssessmentTypes.quality,
                      },
                    });
                    break;
                  case 'moc':
                    openModal({
                      modalName: modals.createAssessment,
                      variables: {
                        participants: [user],
                        types: [defaultAssessmentTypes.moc],
                        type: defaultAssessmentTypes.moc,
                      },
                    });
                    break;
                  case 'templates':
                    openModal({
                      modalName: modals.createTemplate,
                      variables: {
                        types: [
                          defaultAssessmentTypes.quality,
                          defaultAssessmentTypes.moc,
                        ],
                        type: defaultAssessmentTypes.quality,
                      },
                    });
                    break;
                  default:
                    throw new Error(`Invalid type ${tab}`);
                }
              }}
            />
          )}
          {tab === defaultTab && (isAdmin || isWorkspaceAdmin) && (
            <Button
              outlined
              value={t(keys.action.EXPORT)}
              onClick={handleExport}
              icon="file_upload"
            />
          )}
        </div>
      </div>
      <TabSystem type={tab} options={options} />
      <ProgressModal
        show={isFetchingAllData}
        progress={progress}
        title="Exporting Data"
        message="One moment while we fetch your data - Please don't close this window!"
        icon="import_export"
      />
    </div>
  );
}
